import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { tendApi, Tendv2ContentItem } from '../api/tendApi';
import { brandApi } from '../api/brandApi';
import { contentAdapter } from '../api/adapters';
import { getApiUrl } from '../config/api';
import { Logo, mapDefaultLogo } from '../api/logoApi';
import { useAuth } from '../AuthContext';
import TendedContentGrid from '../components/tend/TendedContentGrid';
import { authedFetch } from '../utils/authedFetch';

interface TendPreviewPageParams extends Record<string, string | undefined> {
  slug: string;
  contentId: string;
}

interface ContentMeta {
  title?: string;
  brandId?: number;
  brandName?: string;
  brandDefaultLogo?: Logo | null;
  brandLogoDisplayMode?: 'icon_only' | 'name_only' | 'icon_and_name';
}

const TendPreviewPage: React.FC = () => {
  const { slug, contentId: contentIdParam } = useParams<TendPreviewPageParams>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const contentId = contentIdParam ? parseInt(contentIdParam, 10) : 0;

  const [activeTab, setActiveTab] = useState<'seed' | 'tendv2'>('tendv2');
  const [items, setItems] = useState<Tendv2ContentItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contentMeta, setContentMeta] = useState<ContentMeta>({});
  const [curatorBrandId, setCuratorBrandId] = useState<number | undefined>(undefined);
  const [isLinkCopied, setIsLinkCopied] = useState(false);

  const isSuperAdmin = user?.is_super_admin || false;

  useEffect(() => {
    if (!slug || !contentId) return;

    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem('access_token');

        const contentPromise = authedFetch(getApiUrl(`/content/${contentId}`), {
          headers: { Authorization: `Bearer ${token}` },
        }).then(async (res) => {
          if (!res.ok) {
            if (res.status === 403 || res.status === 404) {
              throw new Error('access_denied');
            }
            throw new Error(`Failed to load content (${res.status})`);
          }
          return res.json();
        });

        const brandPromise = brandApi.getBrand(slug).catch(() => null);
        const itemsPromise = tendApi.getTendV2ContentItems(contentId);

        const [contentData, brandData, tendItems] = await Promise.all([
          contentPromise,
          brandPromise,
          itemsPromise,
        ]);

        if (cancelled) return;

        const adapted: any = contentAdapter.toDisplayFormat(contentData);

        // Derive brand fields from brand-by-slug (reliable) with adapter as fallback.
        const brandIdFromSlug = brandData?.brand_id ?? brandData?.id;
        const resolvedBrandId = adapted.brandId ?? (brandIdFromSlug ? Number(brandIdFromSlug) : undefined);
        const resolvedBrandName = adapted.brandName || brandData?.name;
        const resolvedDefaultLogo = adapted.brandDefaultLogo || mapDefaultLogo(brandData?.default_logo);
        const resolvedLogoDisplayMode = adapted.brandLogoDisplayMode || brandData?.logo_display_mode || 'icon_and_name';

        setContentMeta({
          title: adapted.title,
          brandId: resolvedBrandId,
          brandName: resolvedBrandName,
          brandDefaultLogo: resolvedDefaultLogo,
          brandLogoDisplayMode: resolvedLogoDisplayMode,
        });

        if (brandIdFromSlug) {
          setCuratorBrandId(Number(brandIdFromSlug));
        }

        setItems(tendItems);
      } catch (err: any) {
        if (cancelled) return;
        if (err?.message === 'access_denied') {
          navigate(`/${slug}`);
          return;
        }
        console.error('[TendPreviewPage] Error loading:', err);
        setError(err?.message || 'Failed to load tended content');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [slug, contentId, navigate]);

  const handleCopyLink = useCallback(async () => {
    const url = `${window.location.origin}/${slug}/tend/${contentId}`;
    try {
      await navigator.clipboard.writeText(url);
      setIsLinkCopied(true);
      setTimeout(() => setIsLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy share link:', err);
    }
  }, [slug, contentId]);

  if (!slug || !contentId) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <p>Invalid URL.</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4 min-w-0">
            <Link
              to={`/${slug}`}
              className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to {contentMeta.brandName || slug}
            </Link>
            {contentMeta.title && (
              <h1 className="text-lg font-semibold text-gray-100 truncate">
                {contentMeta.title}
              </h1>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Tab Navigation */}
            <div className="flex items-center gap-1 bg-gray-900 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('tendv2')}
                className={`px-4 py-1.5 rounded-md font-medium text-sm transition-colors ${
                  activeTab === 'tendv2'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                TEND
              </button>
              <button
                onClick={() => setActiveTab('seed')}
                className={`px-4 py-1.5 rounded-md font-medium text-sm transition-colors ${
                  activeTab === 'seed'
                    ? 'bg-gray-700 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-gray-800'
                }`}
              >
                SEED
              </button>
            </div>
            <button
              onClick={handleCopyLink}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded transition-colors"
              title="Copy share link to this page"
            >
              {isLinkCopied ? (
                <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656-5.656M10.172 13.828a4 4 0 01-5.656-5.656l3-3a4 4 0 015.656 5.656" />
                </svg>
              )}
            </button>
          </div>
        </div>

        {/* Grid body */}
        <div className="bg-white rounded-xl p-6 min-h-[400px]">
          {error ? (
            <div className="text-center py-12 text-red-600">
              <p className="font-semibold mb-2">Couldn't load tended content</p>
              <p className="text-sm">{error}</p>
            </div>
          ) : (
            <TendedContentGrid
              contentId={contentId}
              activeTab={activeTab}
              items={items}
              isLoading={isLoading}
              brandId={contentMeta.brandId}
              brandName={contentMeta.brandName}
              brandSlug={slug}
              contentTitle={contentMeta.title}
              brandDefaultLogo={contentMeta.brandDefaultLogo}
              brandLogoDisplayMode={contentMeta.brandLogoDisplayMode}
              curatorBrandId={curatorBrandId}
              isSuperAdmin={isSuperAdmin}
              onItemsChange={setItems}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TendPreviewPage;
